import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../utils/api';

const AUTO_MORPH_MS = 5000;
const MORPH_DURATION_MS = 550;
const REWARD_CELL_WIDTH = 140;
const SPIN_CYCLES = 3; // Full cycles before landing
const SPIN_DURATION_S = 4;

function resolveImageUrl(url) {
  if (!url) return url;
  const baseUrl = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
  return url.startsWith('/') && baseUrl ? `${baseUrl}${url}` : url;
}

export default function RewardsCardModal({
  businessId,
  points,
  pointsCap,
  pointsJustEarned = 0,
  referralCount,
  referralsNeeded,
  availableRewards = [],
  canClaim = false,
  rewardsClaimedCount = 0,
  lastClaimedRewards = [],
  getToken,
  onClose,
  onClaimSuccess,
  showCoins = true,
}) {
  const [displayPoints, setDisplayPoints] = useState(points - pointsJustEarned);
  const [mounted, setMounted] = useState(false);
  const [isMorphing, setIsMorphing] = useState(false);
  const morphTimerRef = useRef(null);
  const autoMorphTimerRef = useRef(null);
  const hasStartedMorphRef = useRef(false);
  const [claimView, setClaimView] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState(null);
  const [selectedRewardType, setSelectedRewardType] = useState(null);
  const [selectedMenuItemId, setSelectedMenuItemId] = useState(null);

  const rewards = availableRewards.length > 0 ? availableRewards : [
    { type: '10_percent_off', label: '10% off next meal', imageUrl: null },
    { type: 'free_drink', label: 'Free drink', imageUrl: null },
    { type: 'free_side', label: 'Free side', imageUrl: null },
  ];

  // Pick final reward on mount for wheel spin
  const finalRewardIndexRef = useRef(null);
  if (finalRewardIndexRef.current === null && rewards.length > 0) {
    finalRewardIndexRef.current = Math.floor(Math.random() * rewards.length);
  }
  const finalRewardIndex = finalRewardIndexRef.current ?? 0;
  const currentReward = rewards[finalRewardIndex % rewards.length];

  useEffect(() => {
    setMounted(true);
  }, []);

  const startMorph = useCallback(() => {
    if (hasStartedMorphRef.current) return;
    hasStartedMorphRef.current = true;
    if (autoMorphTimerRef.current) {
      clearTimeout(autoMorphTimerRef.current);
      autoMorphTimerRef.current = null;
    }
    if (morphTimerRef.current) clearTimeout(morphTimerRef.current);
    setIsMorphing(true);
    morphTimerRef.current = setTimeout(() => {
      onClose?.();
    }, MORPH_DURATION_MS);
  }, [onClose]);

  // Auto-dismiss after 5s only when user cannot claim (no reward to select). If canClaim, keep card up so they can pick a reward.
  useEffect(() => {
    const cleanup = () => {
      if (autoMorphTimerRef.current) clearTimeout(autoMorphTimerRef.current);
      if (morphTimerRef.current) clearTimeout(morphTimerRef.current);
    };
    if (!mounted || canClaim) return cleanup;
    autoMorphTimerRef.current = setTimeout(startMorph, AUTO_MORPH_MS);
    return cleanup;
  }, [mounted, canClaim, startMorph]);

  useEffect(() => {
    if (!mounted) return;
    const target = points;
    const start = points - pointsJustEarned;
    const duration = 800;
    const startTime = Date.now();

    const tick = () => {
      const elapsed = Date.now() - startTime;
      const p = Math.min(1, elapsed / duration);
      const eased = 1 - (1 - p) ** 2;
      setDisplayPoints(Math.round(start + (target - start) * eased));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [mounted, points, pointsJustEarned]);

  const handleClaimClick = useCallback(() => {
    if (canClaim && !claimView) {
      setClaimView(true);
      setClaimError(null);
      return;
    }
    if (canClaim && claimView && !selectedRewardType) {
      setClaimView(false);
      return;
    }
    if (claimView && selectedRewardType) {
      setClaiming(true);
      setClaimError(null);
      api('/api/customers/me/rewards/claim', {
        getToken,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessId,
          rewardType: selectedRewardType,
          menuItemId: selectedMenuItemId || undefined,
        }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.error) throw new Error(data.error);
          onClaimSuccess?.(data);
          startMorph();
        })
        .catch((err) => {
          setClaimError(err.message || 'Failed to claim');
        })
        .finally(() => setClaiming(false));
      return;
    }
    // User clicked "Got it" - close immediately and redirect (skip morph so overlay doesn't get stuck)
    onClose?.();
  }, [canClaim, claimView, selectedRewardType, selectedMenuItemId, businessId, getToken, onClaimSuccess, startMorph]);

  const pct = pointsCap > 0 ? Math.min(100, (displayPoints / pointsCap) * 100) : 0;
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

  // Rain: coins pour from top of card, fall straight down with slight X spread
  const coinRainDrops = useMemo(() => {
    const drops = [];
    for (let i = 0; i < 10; i++) {
      drops.push({
        x: -120 + Math.random() * 240, // px offset from center, spread across card
        delay: Math.random() * 0.8,
        duration: 2.2 + Math.random() * 0.8,
      });
    }
    return drops;
  }, []); // Fresh each time modal mounts

  const modalContent = (
      <div
        className={`fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-8 pb-12 bg-black/60 rewards-morph-backdrop ${isMorphing ? 'rewards-morphing' : ''}`}
        role="dialog"
      aria-modal="true"
      aria-labelledby="rewards-modal-title"
    >
      {showCoins && !prefersReducedMotion && !isMorphing && (
        <div className="coin-rain-wrapper overflow-visible" style={{ top: '22%' }}>
          {coinRainDrops.map((d, i) => (
            <span
              key={i}
              className="coin-rain coin-rain-fall"
              style={{
                left: '50%',
                '--rx': `${d.x}px`,
                animationDelay: `${d.delay}s`,
                animationDuration: `${d.duration}s`,
              }}
            >
              🪙
            </span>
          ))}
        </div>
      )}
      <div
        className={`rewards-morph-card relative w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl border-2 border-amber-400/60 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-slate-800 dark:to-slate-900 dark:border-amber-500/40 ${isMorphing ? 'rewards-morphing' : ''}`}
        style={{
          transform: isMorphing ? undefined : 'perspective(800px) rotateX(-2deg)',
          boxShadow: isMorphing ? undefined : '0 25px 50px -12px rgba(0,0,0,0.5), 0 0 0 1px rgba(251,191,36,0.3)',
        }}
      >

        <div className="p-6 pt-8 pb-8">
          <h2 id="rewards-modal-title" className="text-xl font-bold text-center text-slate-900 dark:text-white mb-4">
            {claimView ? 'Claim your reward!' : pointsJustEarned > 0 ? "You've earned points!" : 'Your rewards'}
          </h2>

          {/* Prize display - horizontal wheel spin (left to right, slows down to land) */}
          {!claimView && rewards.length > 0 && (
            <div className="mb-5 relative overflow-hidden rounded-xl border-2 border-amber-300/50 bg-white/50 dark:bg-slate-800/50 p-4 min-h-[120px] flex items-center justify-center">
              {rewards.length === 1 ? (
                <div className="flex flex-col items-center justify-center">
                  {currentReward?.imageUrl ? (
                    <img
                      src={resolveImageUrl(currentReward.imageUrl)}
                      alt=""
                      className="w-16 h-16 object-cover rounded-lg mb-2"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-amber-200/50 dark:bg-amber-900/30 flex items-center justify-center mb-2">
                      <span className="text-2xl">🎁</span>
                    </div>
                  )}
                  <span className="text-sm font-semibold text-slate-900 dark:text-white text-center">
                    {currentReward?.label ?? 'Reward'}
                  </span>
                </div>
              ) : (
                <div
                  className="reward-wheel-viewport"
                  style={{ width: REWARD_CELL_WIDTH }}
                >
                  <div
                    className="reward-wheel-strip"
                    style={{
                      '--reward-cell-width': `${REWARD_CELL_WIDTH}px`,
                      '--reward-wheel-end': `-${(SPIN_CYCLES * rewards.length + finalRewardIndex) * REWARD_CELL_WIDTH}px`,
                      '--reward-wheel-duration': `${SPIN_DURATION_S}s`,
                    }}
                  >
                    {[...Array(SPIN_CYCLES + 2)].flatMap((_, cycle) =>
                      rewards.map((r, i) => (
                        <div
                          key={`${cycle}-${i}`}
                          className="reward-wheel-cell"
                          style={{ width: REWARD_CELL_WIDTH, minWidth: REWARD_CELL_WIDTH }}
                        >
                          {r.imageUrl ? (
                            <img
                              src={resolveImageUrl(r.imageUrl)}
                              alt=""
                              className="w-16 h-16 object-cover rounded-lg mx-auto mb-2 block"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-lg bg-amber-200/50 dark:bg-amber-900/30 flex items-center justify-center mb-2 mx-auto">
                              <span className="text-2xl">🎁</span>
                            </div>
                          )}
                          <span className="text-sm font-semibold text-slate-900 dark:text-white text-center block truncate px-1">
                            {r.label ?? 'Reward'}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Claim flow: reward picker */}
          {claimView && (
            <div className="mb-5 space-y-2 max-h-48 overflow-y-auto">
              {rewards.map((r) => (
                <button
                  key={r.type + (r.menuItemId ?? '')}
                  type="button"
                  onClick={() => {
                    setSelectedRewardType(r.type);
                    setSelectedMenuItemId(r.menuItemId || null);
                  }}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                    selectedRewardType === r.type
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                      : 'border-slate-200 dark:border-slate-600 hover:border-amber-300'
                  }`}
                >
                  {r.imageUrl ? (
                    <img src={resolveImageUrl(r.imageUrl)} alt="" className="w-12 h-12 object-cover rounded-lg" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-amber-200/50 flex items-center justify-center text-xl">🎁</div>
                  )}
                  <span className="font-medium text-slate-900 dark:text-white">{r.label}</span>
                </button>
              ))}
            </div>
          )}

          <div className="mb-4">
            <div className="flex justify-between text-sm text-slate-600 dark:text-slate-400 mb-2">
              <span>Points</span>
              <span>
                <span className="font-bold text-amber-600 dark:text-amber-400">{displayPoints}</span> / {pointsCap}
              </span>
            </div>
            <div className="h-3 rounded-full bg-slate-200 dark:bg-slate-600 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-600 transition-all duration-500 ease-out"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          {rewardsClaimedCount > 0 && lastClaimedRewards?.length > 0 && (
            <p className="text-xs text-slate-500 dark:text-slate-400 text-center mb-3">
              Rewards earned: {lastClaimedRewards.map((r) => r.label).join(', ')}
            </p>
          )}

          {referralsNeeded != null && referralCount != null && referralsNeeded > 0 && !claimView && (
            <p className="text-sm text-slate-600 dark:text-slate-400 text-center mb-4">
              Referrals: {referralCount} / {referralsNeeded}
            </p>
          )}

          {claimError && <p className="text-sm text-red-600 dark:text-red-400 mb-2 text-center">{claimError}</p>}

          <button
            type="button"
            onClick={handleClaimClick}
            disabled={claiming || isMorphing}
            className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-semibold"
          >
            {claiming ? 'Claiming…' : isMorphing ? '' : canClaim && claimView ? (selectedRewardType ? 'Claim reward' : 'Back') : canClaim && !claimView ? 'Claim my reward' : 'Got it'}
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(modalContent, document.body)
    : modalContent;
}
